import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
    FiUsers, FiMail, FiDollarSign, FiPercent, FiCreditCard, 
    FiSend, FiPlus, FiEdit3, FiFileText, FiEye
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

// API configuration
const API_URL = process.env.REACT_APP_API_URL || '/api';

const Container = styled.div`
    padding: ${props => props.theme.spacing.xl};
    max-width: 1400px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
    font-size: ${props => props.theme.fontSizes.xxl};
    font-weight: 700;
    color: ${props => props.theme.colors.gray[800]};
`;

const HeaderButtons = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing.md};
`;

const Button = styled.button`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    background: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.success};
    color: white;
    border: none;
    border-radius: ${props => props.theme.borders.radius.md};
    cursor: pointer;
    font-size: ${props => props.theme.fontSizes.sm};
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }
`;

const StatsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${props => props.theme.spacing.lg};
    margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
    background: white;
    padding: ${props => props.theme.spacing.lg};
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    text-align: center;
`;

const StatNumber = styled.div`
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
`;

const StatLabel = styled.div`
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[600]};
`;

const PromotersTable = styled.div`
    background: white;
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.sm};
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr;
    gap: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.lg};
    background: ${props => props.theme.colors.gray[50]};
    font-weight: 600;
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[700]};
    border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

const TableRow = styled.div`
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr;
    gap: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.lg};
    border-bottom: 1px solid ${props => props.theme.colors.gray[100]};
    align-items: center;

    &:hover {
        background: ${props => props.theme.colors.gray[25]};
    }
`;

const PromoterInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const PromoterName = styled.div`
    font-weight: 600;
    color: ${props => props.theme.colors.gray[800]};
    margin-bottom: ${props => props.theme.spacing.xs};
`;

const ReferenceId = styled.div`
    font-size: ${props => props.theme.fontSizes.xs};
    color: ${props => props.theme.colors.gray[500]};
`;

const ActionButtons = styled.div`
    display: flex;
    gap: ${props => props.theme.spacing.xs};
    flex-wrap: wrap;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.xs};
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    background: ${props => {
        if (props.variant === 'percentage') return props.theme.colors.warning;
        if (props.variant === 'email') return props.theme.colors.info;
        if (props.variant === 'bank') return props.theme.colors.success;
        if (props.variant === 'payout') return props.theme.colors.primary;
        return props.theme.colors.gray[500];
    }};
    color: white;
    border: none;
    border-radius: ${props => props.theme.borders.radius.sm};
    cursor: pointer;
    font-size: ${props => props.theme.fontSizes.xs};
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    padding: ${props => props.theme.spacing.xl};
    border-radius: ${props => props.theme.borders.radius.lg};
    box-shadow: ${props => props.theme.shadows.lg};
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing.lg};
`;

const ModalTitle = styled.h2`
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: 700;
    color: ${props => props.theme.colors.gray[800]};
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: ${props => props.theme.fontSizes.xl};
    cursor: pointer;
    color: ${props => props.theme.colors.gray[500]};
    
    &:hover {
        color: ${props => props.theme.colors.gray[700]};
    }
`;

const FormGroup = styled.div`
    margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
    display: block;
    margin-bottom: ${props => props.theme.spacing.sm};
    font-weight: 600;
    color: ${props => props.theme.colors.gray[700]};
`;

const Input = styled.input`
    width: 100%;
    padding: ${props => props.theme.spacing.sm};
    border: 1px solid ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borders.radius.md};
    font-size: ${props => props.theme.fontSizes.sm};

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.primary};
    }
`;

const TextArea = styled.textarea`
    width: 100%;
    padding: ${props => props.theme.spacing.sm};
    border: 1px solid ${props => props.theme.colors.gray[300]};
    border-radius: ${props => props.theme.borders.radius.md};
    font-size: ${props => props.theme.fontSizes.sm};
    min-height: 200px;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: ${props => props.theme.colors.primary};
    }
`;

const LoadingSpinner = styled.div`
    text-align: center;
    padding: ${props => props.theme.spacing.xl};
    color: ${props => props.theme.colors.gray[600]};
`;

const PromotersManagement = () => {
    const { token } = useAuth();
    const [promoters, setPromoters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPercentageModal, setShowPercentageModal] = useState(false);
    const [selectedPromoter, setSelectedPromoter] = useState(null);
    const [termsAndConditions, setTermsAndConditions] = useState('');
    const [percentages, setPercentages] = useState({
        men: 0,
        women: 0,
        couples: 0
    });
    const [stats, setStats] = useState({
        totalPromoters: 0,
        activePromoters: 0,
        totalEarnings: 0,
        pendingPayouts: 0
    });

    useEffect(() => {
        fetchPromoters();
        // fetchTermsAndConditions(); // TODO: Implement terms endpoint in admin API
    }, []);

    const fetchPromoters = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/admin/promoters`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                const promotersData = data.promoters || [];
                setPromoters(promotersData);
                
                // Calculate stats from the promoters data
                const totalPromoters = promotersData.length;
                const activePromoters = promotersData.filter(p => p.isActive).length;
                const totalEarnings = promotersData.reduce((total, p) => total + (p.totalEarnings || 0), 0);
                const pendingPayouts = promotersData.reduce((total, p) => total + (p.pendingPayout || 0), 0);
                
                setStats({
                    totalPromoters,
                    activePromoters,
                    totalEarnings,
                    pendingPayouts
                });
            }
        } catch (error) {
            console.error('Error fetching promoters:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTermsAndConditions = async () => {
        // TODO: Implement terms endpoint in admin API
        // For now, use placeholder text
        setTermsAndConditions('Terms and conditions will be managed here once the admin API endpoint is implemented.');
    };

    const saveTermsAndConditions = async () => {
        // TODO: Implement terms endpoint in admin API
        alert('Terms and conditions functionality not yet implemented in admin API');
    };

    const openPercentageModal = (promoter) => {
        setSelectedPromoter(promoter);
        setPercentages({
            men: promoter.commissionRates?.menPercentage || 0,
            women: promoter.commissionRates?.ladiesPercentage || 0,
            couples: promoter.commissionRates?.couplePercentage || 0
        });
        setShowPercentageModal(true);
    };

    const savePercentages = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/promoters/${selectedPromoter.referenceId}/commission-rates`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ladiesPercentage: percentages.ladies,
                    menPercentage: percentages.men,
                    couplePercentage: percentages.couple
                })
            });
            
            if (response.ok) {
                alert('Percentages updated successfully!');
                setShowPercentageModal(false);
                fetchPromoters(); // Refresh the list
            }
        } catch (error) {
            console.error('Error saving percentages:', error);
            alert('Error saving percentages');
        }
    };

    const handleEmailClick = (promoter) => {
        // Placeholder for email functionality
        alert(`Email functionality for ${promoter.name} - Coming soon!`);
    };

    const handleBankDetailsClick = (promoter) => {
        // Placeholder for bank details functionality
        alert(`Bank details for ${promoter.name} - Coming soon!`);
    };

    const handlePayoutRequestClick = (promoter) => {
        // Placeholder for payout request functionality
        alert(`Payout request for ${promoter.name} - Coming soon!`);
    };

    if (loading) {
        return (
            <Container>
                <LoadingSpinner>Loading promoters...</LoadingSpinner>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <Title>Promoters Management</Title>
                <HeaderButtons>
                    <Button primary onClick={() => setShowTermsModal(true)}>
                        <FiFileText />
                        Terms & Conditions
                    </Button>
                </HeaderButtons>
            </Header>

            {/* Statistics */}
            <StatsContainer>
                <StatCard>
                    <StatNumber>{stats.totalPromoters}</StatNumber>
                    <StatLabel>Total Promoters</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>{stats.activePromoters}</StatNumber>
                    <StatLabel>Active Promoters</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>${stats.totalEarnings?.toLocaleString() || 0}</StatNumber>
                    <StatLabel>Total Earnings</StatLabel>
                </StatCard>
                <StatCard>
                    <StatNumber>{stats.pendingPayouts}</StatNumber>
                    <StatLabel>Pending Payouts</StatLabel>
                </StatCard>
            </StatsContainer>

            {/* Promoters Table */}
            <PromotersTable>
                <TableHeader>
                    <div>Promoter Name</div>
                    <div>Reference ID</div>
                    <div>Total Earnings</div>
                    <div>Men Earnings</div>
                    <div>Women Earnings</div>
                    <div>Couples Earnings</div>
                    <div>Men Referred</div>
                    <div>Women Referred</div>
                    <div>Couples Referred</div>
                    <div>Actions</div>
                </TableHeader>

                {promoters.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                        No promoters found
                    </div>
                ) : (
                    promoters.map((promoter) => (
                        <TableRow key={promoter._id}>
                            <PromoterInfo>
                                <PromoterName>{promoter.name || 'N/A'}</PromoterName>
                                <ReferenceId>ID: {promoter.referenceId || 'N/A'}</ReferenceId>
                            </PromoterInfo>
                            <div>${promoter.earnings?.total?.toLocaleString() || '0'}</div>
                            <div>${promoter.earnings?.men?.toLocaleString() || '0'}</div>
                            <div>${promoter.earnings?.women?.toLocaleString() || '0'}</div>
                            <div>${promoter.earnings?.couples?.toLocaleString() || '0'}</div>
                            <div>{promoter.referrals?.men || 0}</div>
                            <div>{promoter.referrals?.women || 0}</div>
                            <div>{promoter.referrals?.couples || 0}</div>
                            <ActionButtons>
                                <ActionButton 
                                    variant="percentage" 
                                    onClick={() => openPercentageModal(promoter)}
                                >
                                    <FiPercent />
                                    %
                                </ActionButton>
                                <ActionButton 
                                    variant="email" 
                                    onClick={() => handleEmailClick(promoter)}
                                >
                                    <FiMail />
                                    Email
                                </ActionButton>
                                <ActionButton 
                                    variant="bank" 
                                    onClick={() => handleBankDetailsClick(promoter)}
                                >
                                    <FiCreditCard />
                                    Bank
                                </ActionButton>
                                <ActionButton 
                                    variant="payout" 
                                    onClick={() => handlePayoutRequestClick(promoter)}
                                >
                                    <FiDollarSign />
                                    Payout
                                </ActionButton>
                            </ActionButtons>
                        </TableRow>
                    ))
                )}
            </PromotersTable>

            {/* Terms & Conditions Modal */}
            {showTermsModal && (
                <Modal onClick={() => setShowTermsModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Promoters Terms & Conditions</ModalTitle>
                            <CloseButton onClick={() => setShowTermsModal(false)}>×</CloseButton>
                        </ModalHeader>
                        <FormGroup>
                            <Label>Terms and Conditions Content</Label>
                            <TextArea
                                value={termsAndConditions}
                                onChange={(e) => setTermsAndConditions(e.target.value)}
                                placeholder="Enter the terms and conditions for promoters..."
                            />
                        </FormGroup>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setShowTermsModal(false)}>Cancel</Button>
                            <Button primary onClick={saveTermsAndConditions}>Save Terms</Button>
                        </div>
                    </ModalContent>
                </Modal>
            )}

            {/* Percentage Management Modal */}
            {showPercentageModal && selectedPromoter && (
                <Modal onClick={() => setShowPercentageModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Percentage Management - {selectedPromoter.name}</ModalTitle>
                            <CloseButton onClick={() => setShowPercentageModal(false)}>×</CloseButton>
                        </ModalHeader>
                        <FormGroup>
                            <Label>Men Percentage (%)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={percentages.men}
                                onChange={(e) => setPercentages({...percentages, men: Number(e.target.value)})}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Women Percentage (%)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={percentages.women}
                                onChange={(e) => setPercentages({...percentages, women: Number(e.target.value)})}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Couples Percentage (%)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="100"
                                value={percentages.couples}
                                onChange={(e) => setPercentages({...percentages, couples: Number(e.target.value)})}
                            />
                        </FormGroup>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setShowPercentageModal(false)}>Cancel</Button>
                            <Button primary onClick={savePercentages}>Save Percentages</Button>
                        </div>
                    </ModalContent>
                </Modal>
            )}
        </Container>
    );
};

export default PromotersManagement;
